import React, { useState } from 'react';
import { Play, ChevronDown, Factory, Truck, Award } from 'lucide-react';
import { companyInfo } from '../data/mock';

const HeroSection = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {/* Fallback gradient while video loads */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" />
        
        {/* Video Element - Paper Manufacturing Stock Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-40' : 'opacity-0'
          }`}
        >
          {/* Using Pexels free stock video of paper/cardboard manufacturing */}
          <source
            src="https://videos.pexels.com/video-files/5532770/5532770-uhd_2560_1440_25fps.mp4"
            type="video/mp4"
          />
        </video>
        
        {/* Overlay gradients for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/90 via-neutral-950/70 to-neutral-950/90" />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-neutral-950/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 border border-amber-400/30 rounded-full mb-8 animate-fade-in">
            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <span className="text-amber-400 text-sm font-medium">Perth's Leading Paper Core Manufacturer</span>
          </div>

          {/* Main Heading - SEO Optimized */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
            <span className="block">Paper Core</span>
            <span className="block">Manufacturer</span>
            <span className="block text-amber-400">Western Australia</span>
          </h1>

          {/* Tagline */}
          <p className="text-xl sm:text-2xl text-neutral-300 font-light mb-4 italic">
            "{companyInfo.tagline}"
          </p>

          {/* Description - SEO Rich */}
          <p className="text-lg text-neutral-400 max-w-2xl mb-10 leading-relaxed">
            Adela Merchants supplies premium cardboard cores, paper tubes, and print supplies across Perth and Western Australia. 
            Custom manufacturing, wholesale pricing, and fast delivery for industrial, printing, and construction applications.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button
              onClick={() => scrollToSection('#contact')}
              className="group px-8 py-4 bg-amber-400 text-neutral-900 font-semibold rounded-lg hover:bg-amber-300 transition-all duration-300 flex items-center justify-center gap-2"
            >
              Request a Quote
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
            <button
              onClick={() => scrollToSection('#products')}
              className="px-8 py-4 bg-transparent border-2 border-neutral-600 text-white font-semibold rounded-lg hover:border-amber-400 hover:text-amber-400 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              View Products
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 pt-8 border-t border-neutral-800">
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <Factory className="w-5 h-5 text-amber-400" />
                <span className="text-3xl font-bold text-white">100%</span>
              </div>
              <p className="text-neutral-500 text-sm">Australian Made</p>
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <Truck className="w-5 h-5 text-amber-400" />
                <span className="text-3xl font-bold text-white">WA</span>
              </div>
              <p className="text-neutral-500 text-sm">Wide Delivery</p>
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <Award className="w-5 h-5 text-amber-400" />
                <span className="text-3xl font-bold text-white">Custom</span>
              </div>
              <p className="text-neutral-500 text-sm">Sizes Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <button
          onClick={() => scrollToSection('#about')}
          className="flex flex-col items-center gap-2 text-neutral-500 hover:text-amber-400 transition-colors"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
